/**
 * Playhead - Draggable red playhead line that moves across the timeline
 * Uses transform for smooth GPU-accelerated animation
 */
import { useState, useCallback, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';

export const Playhead = forwardRef(function Playhead({
  position,
  pxPerMs,
  labelWidth,
  scrollLeft,
  totalDuration,
  onSeek,
  containerRef
}, ref) {
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, position: 0 });
  const playheadRef = useRef(null);
  
  // Use refs to get latest values in setPosition without stale closures
  const pxPerMsRef = useRef(pxPerMs);
  const labelWidthRef = useRef(labelWidth);
  const scrollLeftRef = useRef(scrollLeft);
  
  // Keep refs updated
  useEffect(() => {
    pxPerMsRef.current = pxPerMs;
    labelWidthRef.current = labelWidth;
    scrollLeftRef.current = scrollLeft;
  }, [pxPerMs, labelWidth, scrollLeft]);
  
  // Expose method to update position directly without re-render
  useImperativeHandle(ref, () => ({
    setPosition: (positionMs) => {
      if (playheadRef.current) {
        // Use refs to get latest values, avoiding stale closures
        const leftPosition = (positionMs * pxPerMsRef.current) + labelWidthRef.current - scrollLeftRef.current;
        playheadRef.current.style.transform = `translateX(${leftPosition}px)`;
      }
    }
  }), []); // Empty deps - refs handle the updates
  
  const leftPosition = (position * pxPerMs) + labelWidth - scrollLeft;
  
  // Handle mouse down on playhead
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      position: position
    };
  }, [position]);

  // Handle mouse move during drag
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - dragStartRef.current.x;
      const deltaMs = deltaX / pxPerMs;
      const newPosition = dragStartRef.current.position + deltaMs;
      const clampedPosition = Math.max(0, Math.min(newPosition, totalDuration));
      onSeek?.(clampedPosition);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, pxPerMs, totalDuration, onSeek]);

  // Don't render if off-screen
  if (leftPosition < labelWidth - 20 || leftPosition > window.innerWidth + 20) {
    return null;
  }

  return (
    <div
      ref={playheadRef}
      className="absolute top-0 bottom-0 z-50"
      style={{ 
        left: 0,
        transform: `translateX(${leftPosition}px)`,
        willChange: 'transform'
      }}
    >
      {/* Triangle head - draggable handle */}
      <div 
        className={`absolute -top-1 -left-2 cursor-ew-resize ${isDragging ? 'scale-110' : 'hover:scale-110'} transition-transform`}
        style={{
          width: 0,
          height: 0,
          borderLeft: '8px solid transparent',
          borderRight: '8px solid transparent',
          borderTop: `10px solid ${isDragging ? '#f87171' : '#ef4444'}`
        }}
        onMouseDown={handleMouseDown}
      />
      {/* Line */}
      <div 
        className={`absolute top-2 bottom-0 w-0.5 pointer-events-none ${isDragging ? 'bg-red-400' : 'bg-red-500'}`}
      />
    </div>
  );
});
